import { useQuery } from '@tanstack/react-query';
import { useUserStore } from '@/store/userStore';
import { 
  getExpiringSubscriptions, 
  getPendingPayments,
  ExpiringSubscription,
  PendingPayment
} from '@/services/edgeFunctionWrappers/dashboardStatsWrapper';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useCurrency } from '@/contexts/CurrencyContext';

export interface DashboardAlert {
  id: string;
  type: 'expiring_subscription' | 'pending_payment' | 'low_stock';
  title: string;
  count: number;
  /** Vrai lorsque la liste renvoyée est tronquée : `count` est un minorant. */
  countIsPartial: boolean;
  /** Libellé prêt à afficher : « 7 » ou « 10+ » si la liste est tronquée. */
  countLabel: string;
  items: {
    id: string;
    label: string;
    detail?: string;
  }[];
  linkTo: string;
  linkLabel: string;
}

// `dashboard-kpis` applique un `.limit(10)` sur les deux listes et ne renvoie
// aucun total : `data.length` n'est donc pas un compte, seulement un minorant.
// Tant que la fonction edge ne renvoie pas de total, on affiche « 10+ » plutôt
// que « 10 » — annoncer « Abonnements expirant : 10 » alors qu'il y en a 47 est
// pire que de ne rien annoncer.
const ALERTS_EDGE_LIMIT = 10;

const formatExpiry = (endDate?: string | null) => {
  const parsed = endDate ? new Date(endDate) : null;
  if (!parsed || isNaN(parsed.getTime())) return 'Date d\'expiration inconnue';
  return `Expire ${formatDistanceToNow(parsed, { addSuffix: true, locale: fr })}`;
};

const buildCount = (length: number) => {
  const countIsPartial = length >= ALERTS_EDGE_LIMIT;
  return {
    count: length,
    countIsPartial,
    countLabel: countIsPartial ? `${ALERTS_EDGE_LIMIT}+` : String(length),
  };
};

export function useDashboardAlerts() {
  const { gymId } = useUserStore();
  const { formatCurrency } = useCurrency();

  const { data: expiringData, isLoading: loadingSubs } = useQuery({
    queryKey: ['dashboard-alerts', 'expiring', gymId],
    queryFn: () => getExpiringSubscriptions(gymId!),
    enabled: !!gymId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: paymentsData, isLoading: loadingPayments } = useQuery({
    queryKey: ['dashboard-alerts', 'payments', gymId],
    queryFn: () => getPendingPayments(gymId!),
    enabled: !!gymId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const alerts: DashboardAlert[] = [];

  if (expiringData && expiringData.length > 0) {
    alerts.push({
      id: 'expiring-subs',
      type: 'expiring_subscription',
      title: 'Abonnements expirant',
      ...buildCount(expiringData.length),
      items: expiringData.slice(0, 3).map((sub: ExpiringSubscription) => ({
        id: sub.id,
        label: sub.user_profiles 
          ? `${sub.user_profiles.first_name || ''} ${sub.user_profiles.last_name || ''}`.trim() || 'Sans nom'
          : 'Sans nom',
        // date-fns v3 lève sur une date invalide : `end_date` est nullable.
        detail: formatExpiry(sub.end_date)
      })),
      linkTo: '/admin/clients?filter=expiring',
      linkLabel: 'Voir tout',
    });
  }

  if (paymentsData && paymentsData.length > 0) {
    alerts.push({
      id: 'pending-payments',
      type: 'pending_payment',
      title: 'Paiements en attente',
      ...buildCount(paymentsData.length),
      items: paymentsData.slice(0, 3).map((payment: PendingPayment) => ({
        id: payment.id,
        label: payment.user_profiles 
          ? `${payment.user_profiles.first_name || ''} ${payment.user_profiles.last_name || ''}`.trim() || 'Sans nom'
          : 'Sans nom',
        detail: `${formatCurrency(payment.amount)} - en attente`
      })),
      linkTo: '/admin/payments?filter=pending',
      linkLabel: 'Voir tout',
    });
  }

  const totalAlerts = alerts.reduce((acc, a) => acc + a.count, 0);

  return {
    alerts,
    totalAlerts,
    isLoading: loadingSubs || loadingPayments,
  };
}
